import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Paper
} from "@mui/material";
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingIcon,
  Security as SecurityIcon,
  EmojiEvents as AwardIcon
} from "@mui/icons-material";

const CleanlinessScore = () => {
  const theme = useTheme();

  const scoreBenefits = [
    "Objective quality measurement for every cleaning",
    "Identify areas for improvement with detailed breakdown",
    "Build trust with property owners through transparency",
    "Data-driven insights to improve cleaning processes",
    "Consistent quality across all your properties"
  ];

  const scoreMetrics = [
    { category: "Surface Cleanliness", score: 98, color: "#4CAF50" },
    { category: "Bathroom Sanitation", score: 96, color: "#2196F3" },
    { category: "Kitchen Hygiene", score: 94, color: "#FF9800" },
    { category: "Organization & Tidiness", score: 97, color: "#9C27B0" }
  ];

  const features = [
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40 }} />,
      title: "AI-Powered Analysis",
      description: "Computer vision technology analyzes photos to detect missed spots and quality issues"
    },
    {
      icon: <TrendingIcon sx={{ fontSize: 40 }} />,
      title: "Trend Analytics",
      description: "Track performance over time and identify patterns in cleaning quality"
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: "Quality Assurance",
      description: "Automatic flagging of substandard cleanings before guests arrive"
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Hero Section */}
      <Box sx={{ textAlign: "center", mb: 8 }}>
        <Chip 
          label="⭐ Cleanliness Score" 
          sx={{ 
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2.5rem", md: "3rem" }
          }}
        >
          Cleanliness Score
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Our AI-powered scoring system provides objective quality measurements for every cleaning, 
          ensuring consistent excellence and building trust with property owners.
        </Typography>
      </Box>

      {/* How It Works */}
      <Grid container spacing={6} sx={{ mb: 8 }}>
        <Grid item xs={12} md={6}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
            How It Works
          </Typography>
          
          <List sx={{ mb: 4 }}>
            {scoreBenefits.map((benefit, index) => (
              <ListItem key={index} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText 
                  primary={benefit}
                  primaryTypographyProps={{ fontWeight: 500 }}
                />
              </ListItem>
            ))}
          </List>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h5" gutterBottom fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: '#FFD700' }} />
              Sample Cleanliness Report
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" fontWeight="500">Overall Score</Typography>
                <Typography variant="body1" fontWeight="600" color={theme.palette.primary.main}>
                  96/100
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={96} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: theme.palette.primary.main
                  }
                }}
              />
            </Box>

            {scoreMetrics.map((metric, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2" fontWeight="500">{metric.category}</Typography>
                  <Typography variant="body2" fontWeight="600" color={metric.color}>
                    {metric.score}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={metric.score} 
                  sx={{ 
                    height: 6, 
                    borderRadius: 3,
                    backgroundColor: alpha(metric.color, 0.1),
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: metric.color
                    }
                  }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>

      {/* Key Features */}
      <Box sx={{ mb: 8 }}>
        <Typography variant="h3" textAlign="center" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Advanced Features
        </Typography>
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card sx={{ textAlign: "center", height: "100%", background: theme.palette.background.paper }}>
                <CardContent sx={{ p: 4 }}>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom fontWeight="600">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color={theme.palette.text.secondary}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Benefits Section */}
      <Paper sx={{ p: 6, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`, borderRadius: 4 }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700 }}>
              Why Cleanliness Score Matters
            </Typography>
            <Typography variant="body1" color={theme.palette.text.secondary} sx={{ mb: 3, lineHeight: 1.7 }}>
              In the short-term rental industry, cleanliness directly impacts guest satisfaction, 
              reviews, and ultimately your revenue. Our scoring system eliminates subjectivity and 
              provides measurable quality standards.
            </Typography>
            <Stack spacing={2}>
              {["5-star reviews increase by 40%", "Guest complaints reduced by 65%", "Repeat bookings up by 25%"].map((stat, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AwardIcon sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="body1" fontWeight="500">{stat}</Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Box
                sx={{
                  width: 200,
                  height: 200,
                  borderRadius: '50%',
                  background: `conic-gradient(${theme.palette.primary.main} 0% 85%, ${alpha(theme.palette.primary.main, 0.2)} 85% 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    width: 160,
                    height: 160,
                    borderRadius: '50%',
                    background: theme.palette.background.paper
                  }
                }}
              >
                <Typography variant="h2" fontWeight="700" color={theme.palette.primary.main} sx={{ position: 'relative', zIndex: 1 }}>
                  4.8★
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
                Average Guest Rating
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default CleanlinessScore;